import type { ReactNode } from "react";
import type { Crumb } from "@/lib/construction/schema";
import { Container } from "@/components/ui/Container";
import { Eyebrow } from "@/components/ui/Eyebrow";
import { RadialBackground } from "@/components/ui/RadialBackground";
import { Breadcrumbs } from "@/components/construction/Breadcrumbs";

type PlaceHeroProps = {
  crumbs: Crumb[];
  eyebrow: ReactNode;
  /** The H1. Pass database values wrapped in their own data-mdlfy-cms element. */
  title: ReactNode;
  /** Answer-first block under the H1 (40–60 words). */
  answer: string;
  children?: ReactNode;
  aside?: ReactNode;
};

/** Hero for region, town and company pages: breadcrumbs, H1 and the answer block. */
export function PlaceHero({ crumbs, eyebrow, title, answer, children, aside }: PlaceHeroProps) {
  return (
    <section className="relative overflow-hidden border-b border-ink/[0.06] bg-surface-mid pb-14 pt-10 md:pb-20 md:pt-14">
      <RadialBackground className="opacity-60" />
      <Container className="relative">
        <Breadcrumbs crumbs={crumbs} />
        <div className="mt-8 grid gap-10 lg:grid-cols-12 lg:items-end">
          <div className={aside ? "lg:col-span-8" : "lg:col-span-10"}>
            <Eyebrow tone="accent">{eyebrow}</Eyebrow>
            <h1 className="mt-6 font-display text-display-xl font-medium text-ink text-balance">{title}</h1>
            <p data-speakable className="mt-6 max-w-3xl text-pretty text-lg text-ink-muted md:text-xl">
              {answer}
            </p>
            {children}
          </div>
          {aside ? <div className="lg:col-span-4">{aside}</div> : null}
        </div>
      </Container>
    </section>
  );
}

/** Section wrapper with a question-style H2. */
export function DirectorySection({
  id,
  title,
  intro,
  children,
  tone = "plain",
}: {
  id: string;
  title: ReactNode;
  intro?: ReactNode;
  children: ReactNode;
  tone?: "plain" | "muted";
}) {
  return (
    <section id={id} aria-labelledby={`${id}-heading`} className={tone === "muted" ? "bg-surface-low/50 py-16 md:py-20" : "py-16 md:py-20"}>
      <Container>
        <h2 id={`${id}-heading`} className="max-w-3xl font-display text-display-lg font-medium text-ink text-balance">
          {title}
        </h2>
        {intro ? <p className="mt-4 max-w-3xl text-lg text-ink-muted">{intro}</p> : null}
        <div className="mt-8">{children}</div>
      </Container>
    </section>
  );
}
